import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES } from '../kanjinData'
import { makeKanjinTag, makeName3D, makeWarmGlow, kanjiNum } from './Proto13'

/**
 * ⑭ 超光速・光のトンネル（ワープ版）— 参考画像準拠のハイパースペース表現。
 * 青・マゼンタ・紫の光条が消失点へ収束する多層ストリークトンネルの中を、
 * 66枚の寄進札と立体文字の建造物名をくぐり抜けて高速で飛ぶ。
 * コース自体は⑬と同じ緩カーブ（酔い対策）で、速度と光の密度だけを上げる。
 */
export class Proto14 extends ProtoBase {
  readonly title = '14. 超光速・光のトンネル（ワープ版）'
  readonly desc = '青×マゼンタの光条が消失点へ流れるハイパースペースを、寄進札と立体文字をくぐって高速走行（コースは⑬と同じ緩カーブ）'

  private curve!: THREE.CatmullRomCurve3
  private u = 0
  private plates: { t: number; name: string; hit: boolean }[] = []
  private layers: { tex: THREE.Texture; speed: number }[] = []
  private names: { group: THREE.Group; baseY: number }[] = []
  private vanish!: THREE.Sprite
  private pulseGeo!: THREE.BufferGeometry
  private static readonly PULSE_N = 46
  private endGlow!: THREE.Sprite
  private endT = 0
  private tmp = new THREE.Vector3()
  private tmp2 = new THREE.Vector3()

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.view.driftEnabled = false
    this.ctx.scene.background = new THREE.Color(0x0d0418)
    this.ctx.scene.fog = new THREE.Fog(0x0d0418, 40, 200)
    this.ctx.deck.setVisible(true)
    if (this.ctx.narrator.loaded) this.ctx.narrator.root.visible = true
    this.ctx.setCaption('光速の彼方へ——六十六の縁が、流れ星になる！')

    // コースは⑬と同じ快適設計
    const pts: THREE.Vector3[] = []
    for (let i = 0; i <= 14; i++) {
      pts.push(new THREE.Vector3(
        Math.sin(i * 0.9) * 16,
        Math.sin(i * 0.55) * 7 - i * 0.9 + (i === 9 ? -9 : 0),
        -i * 46,
      ))
    }
    this.curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.45)

    // ── 多層ストリークトンネル: 半径違いの3層を異速で流し、視差で「光条の奥行き」を作る ──
    // 白飛び対策: ブルーム前提で各層の露出を抑える
    const layerDefs: { radius: number; opacity: number; speed: number; density: number }[] = [
      { radius: 13.5, opacity: 0.32, speed: 1.4, density: 52 },
      { radius: 10.5, opacity: 0.24, speed: 2.3, density: 38 },
      { radius: 8.0, opacity: 0.16, speed: 3.4, density: 26 },
    ]
    for (const d of layerDefs) {
      const tex = Proto14.makeStreakTexture(d.density)
      const tube = new THREE.Mesh(
        new THREE.TubeGeometry(this.curve, 240, d.radius, 20, false),
        new THREE.MeshBasicMaterial({
          map: tex, transparent: true, opacity: d.opacity, side: THREE.BackSide,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      )
      this.group.add(tube)
      this.layers.push({ tex, speed: d.speed })
    }

    // ── 高速で流れる光の粒（青/マゼンタ/白）──
    const N = 900
    const geo = new THREE.BufferGeometry()
    const pos = new Float32Array(N * 3)
    const col = new Float32Array(N * 3)
    const palette = [
      [0.30, 0.62, 1.0], [1.0, 0.18, 0.47], [0.71, 0.30, 1.0], [1.0, 1.0, 1.0], [0.48, 0.84, 1.0],
    ]
    for (let i = 0; i < N; i++) {
      const t = Math.random()
      const p = this.curve.getPointAt(t)
      const ang = Math.random() * Math.PI * 2
      const r = 3 + Math.random() * 10
      pos.set([p.x + Math.cos(ang) * r, p.y + Math.sin(ang) * r, p.z + (Math.random() - 0.5) * 20], i * 3)
      col.set(palette[i % palette.length], i * 3)
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3))
    this.group.add(new THREE.Points(geo, new THREE.PointsMaterial({
      size: 0.34, vertexColors: true, transparent: true, opacity: 0.65,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })))

    // ── 進行方向ガイド: 左右のレール光＋前方へ流れる金のパルス（酔い対策: 道筋の可視化）──
    const railMat = new THREE.LineBasicMaterial({
      color: 0x7bd7ff, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false,
    })
    for (const side of [-1, 1]) {
      const rail: THREE.Vector3[] = []
      for (let i = 0; i <= 320; i++) {
        const t = i / 320
        const p = this.curve.getPointAt(t)
        const tn = this.curve.getTangentAt(t)
        const nrm = new THREE.Vector3(tn.z, 0, -tn.x).normalize()
        rail.push(p.clone().addScaledVector(nrm, side * 2.3).add(new THREE.Vector3(0, 0.35, 0)))
      }
      this.group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(rail), railMat.clone()))
    }
    this.pulseGeo = new THREE.BufferGeometry()
    this.pulseGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(Proto14.PULSE_N * 3), 3))
    const pulses = new THREE.Points(this.pulseGeo, new THREE.PointsMaterial({
      color: 0xffd77a, size: 0.55, transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }))
    pulses.frustumCulled = false
    this.group.add(pulses)

    // ── 66枚の寄進札（ライダー正対）── ワープ中の「実在の記憶」として少し大きめ・光る額
    const texLoader = new THREE.TextureLoader()
    const photoMats = SITES.map((s) => {
      const m = new THREE.MeshBasicMaterial({ color: 0x555577, side: THREE.DoubleSide })
      texLoader.load(`./assets/sites/${s.no}.jpg`, (t) => {
        t.colorSpace = THREE.SRGBColorSpace
        m.map = t
        m.color.set(0xffffff)
        m.needsUpdate = true
      })
      return m
    })
    const frameMat = new THREE.MeshBasicMaterial({
      color: 0x7bd7ff, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending,
    })
    const GOLDEN = 2.399963
    for (let i = 0; i < 66; i++) {
      const t = 0.025 + (i / 66) * 0.93
      const p = this.curve.getPointAt(t)
      const tan = this.curve.getTangentAt(t)
      const n = this.tmp.set(tan.z, 0, -tan.x).normalize().clone()
      const b = new THREE.Vector3().crossVectors(n, tan).normalize()
      const ang = i * GOLDEN
      const off = n.clone().multiplyScalar(Math.cos(ang) * 7.6).addScaledVector(b, Math.sin(ang) * 7.6)

      const site = SITES[i % SITES.length]
      const g = new THREE.Group()
      g.position.copy(p).add(off)
      g.lookAt(g.position.clone().sub(tan)) // 正面向き
      const frame = new THREE.Mesh(new THREE.PlaneGeometry(8.4, 6.5), frameMat)
      frame.position.z = -0.03
      g.add(frame)
      g.add(new THREE.Mesh(new THREE.PlaneGeometry(7.6, 5.7), photoMats[i % photoMats.length]))
      const tag = makeKanjinTag(i + 1, site.building)
      tag.position.set(0, -3.9, 0.05)
      g.add(tag)
      this.group.add(g)
      this.plates.push({ t, name: site.building, hit: false })
    }

    // ── 建造物名の立体文字（ワープ内は白銀×藍の配色）──
    for (let i = 0; i < SITES.length; i++) {
      const t = 0.06 + (i / SITES.length) * 0.86
      const p = this.curve.getPointAt(t)
      const tan = this.curve.getTangentAt(t)
      const g = makeName3D(SITES[i].building, 0xdff2ff, 0x243a6b)
      const baseY = p.y + (i % 2 === 0 ? 4.6 : 3.6)
      g.position.set(p.x, baseY, p.z)
      g.lookAt(p.clone().sub(tan))
      this.group.add(g)
      this.names.push({ group: g, baseY })
    }

    // ── 消失点の光（参考画像の中心光・白飛びしない控えめ露出）──
    this.vanish = Proto14.makeCoreGlow()
    this.vanish.scale.setScalar(28)
    this.group.add(this.vanish)

    // ── 終端 ──
    const end = this.curve.getPointAt(1)
    this.endGlow = makeWarmGlow()
    this.endGlow.position.copy(end).add(new THREE.Vector3(0, 0, -8))
    this.endGlow.scale.setScalar(26)
    this.group.add(this.endGlow)

    this.u = 0
    this.endT = 0
  }

  update(dt: number): void {
    this.elapsed += dt

    // ストリーク層を異速で流す（ワープの主表現）
    for (const l of this.layers) l.tex.offset.x -= dt * l.speed * 0.25

    if (this.endT === 0) {
      // ⑬より速い巡航（全長約34秒）。加減速は緩やか
      const startEase = Math.min(1, this.elapsed / 3.5)
      this.u = Math.min(1, this.u + dt * (1 / 34) * (0.45 + 0.55 * startEase))

      const p = this.curve.getPointAt(this.u)
      const a = this.curve.getPointAt(Math.min(1, this.u + 0.008))
      this.ctx.view.setPose(
        this.tmp.copy(p).add(this.tmp2.set(0, 1.5, 0)),
        a.clone().add(new THREE.Vector3(0, 1.2, 0)),
      )
      // 消失点グローを常に進行方向の先へ
      const ahead = this.curve.getPointAt(Math.min(1, this.u + 0.05))
      this.vanish.position.set(ahead.x, ahead.y + 1.2, ahead.z)
      const vm = this.vanish.material as THREE.SpriteMaterial
      vm.opacity = 0.36 + 0.1 * Math.sin(this.elapsed * 4)

      // 方向パルス: 中央レーンを前方へ流れ、進む向きを示す（ライダーより速い）
      const pp = this.pulseGeo.getAttribute('position') as THREE.BufferAttribute
      for (let i = 0; i < Proto14.PULSE_N; i++) {
        const t = (this.elapsed * 0.045 + i / Proto14.PULSE_N) % 1
        const q = this.curve.getPointAt(t)
        pp.setXYZ(i, q.x, q.y + 0.5, q.z)
      }
      pp.needsUpdate = true

      for (let i = 0; i < this.plates.length; i++) {
        const pl = this.plates[i]
        if (!pl.hit && this.u > pl.t - 0.002) {
          pl.hit = true
          if (i % 6 === 0) {
            this.ctx.sfx.play('chime')
            this.ctx.setCaption(`第${kanjiNum(i + 1)}番 ${pl.name}——`)
          }
        }
      }
      if (this.u >= 1) {
        this.endT = this.elapsed
        this.ctx.sfx.play('drum')
        this.ctx.sfx.play('bell')
        this.ctx.setCaption('光速を抜けて——八十九の木片、一畳へ')
      }
    } else {
      const t = this.elapsed - this.endT
      this.endGlow.scale.setScalar(26 + t * 34)
      if (t > 4.5) {
        this.plates.forEach((pl) => (pl.hit = false))
        this.elapsed = 0
        this.u = 0
        this.endT = 0
        this.endGlow.scale.setScalar(26)
        this.ctx.setCaption('もう一巡——光速の彼方へ！')
      }
    }

    this.names.forEach((n, i) => {
      n.group.position.y = n.baseY + Math.sin(this.elapsed * 1.1 + i * 1.7) * 0.45
      n.group.rotation.z = Math.sin(this.elapsed * 0.7 + i) * 0.03
    })
  }

  protected onDispose(): void {
    this.ctx.view.driftEnabled = true
    this.ctx.scene.fog = null
    this.ctx.deck.setVisible(this.ctx.view.xrActive)
    this.ctx.narrator.root.visible = this.ctx.view.xrActive
  }

  /** 参考画像トーンのストリークテクスチャ: 青/マゼンタ/紫/白の光条（横方向に流す） */
  private static makeStreakTexture(count: number): THREE.Texture {
    const c = document.createElement('canvas')
    c.width = 1024
    c.height = 128
    const g = c.getContext('2d')!
    g.clearRect(0, 0, 1024, 128)
    const colors = ['#4d9fff', '#ff2d78', '#b44dff', '#7bd7ff', '#ff5470', '#ffffff']
    for (let i = 0; i < count; i++) {
      const y = Math.random() * 128
      const h = 0.8 + Math.random() * 3.2
      const x = Math.random() * 1024
      const len = 90 + Math.random() * 480
      const color = colors[i % colors.length]
      const a = 0.16 + Math.random() * 0.34
      const grad = g.createLinearGradient(x, 0, x + len, 0)
      grad.addColorStop(0, 'rgba(0,0,0,0)')
      grad.addColorStop(0.35, `${color}${Math.round(a * 255).toString(16).padStart(2, '0')}`)
      grad.addColorStop(1, 'rgba(0,0,0,0)')
      g.fillStyle = grad
      g.fillRect(x, y, len, h)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(3, 1)
    return tex
  }

  /** 消失点の白熱コア（青白い中心光） */
  private static makeCoreGlow(): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = c.height = 256
    const g = c.getContext('2d')!
    const grad = g.createRadialGradient(128, 128, 0, 128, 128, 128)
    grad.addColorStop(0, 'rgba(255,255,255,0.8)')
    grad.addColorStop(0.22, 'rgba(190,220,255,0.45)')
    grad.addColorStop(0.6, 'rgba(120,90,255,0.18)')
    grad.addColorStop(1, 'rgba(80,40,160,0)')
    g.fillStyle = grad
    g.fillRect(0, 0, 256, 256)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
    }))
  }
}
